import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/styled-system'
import { sharedComponentColorKeys } from '~/components/shared/componentStyles/_colors'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

export enum NotificationBadgeVariant {
  Notification = 'notification',
  Info = 'info',
  FilterHover = 'filterHover',
  Popup = 'popup',
  New = 'new',
  Beta = 'beta',
}

export type NotificationBadgeSize = Size.xs | Size.sm | Size.md

const NotificationBadgeParts = [
  'container',
  'contentContainer',
  'text',
] as const

const NotificationVariantColors =
  sharedComponentColorKeys.NotificationBadge.Notification
const InfoVariantColors = sharedComponentColorKeys.NotificationBadge.Info
const PopupVariantColors = sharedComponentColorKeys.NotificationBadge.Popup
const NewVariantColors = sharedComponentColorKeys.NotificationBadge.New
const BetaVariantColors = sharedComponentColorKeys.NotificationBadge.Beta
const FilterHoverVariantColors =
  sharedComponentColorKeys.NotificationBadge.FilterHover

const helpers = createMultiStyleConfigHelpers(NotificationBadgeParts)

export const NotificationBadgeStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'inline-flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '6.25rem',
    },
    text: {
      textStyle: textStyleKeys.Body4_Bold,
    },
  },
  variants: {
    [NotificationBadgeVariant.Notification]: {
      contentContainer: {
        bg: NotificationVariantColors.Background,
      },
      text: {
        color: NotificationVariantColors.FontColor,
      },
    },
    [NotificationBadgeVariant.Info]: {
      contentContainer: {
        bg: InfoVariantColors.Background,
      },
      text: {
        color: InfoVariantColors.FontColor,
      },
    },
    [NotificationBadgeVariant.FilterHover]: {
      contentContainer: {
        bg: FilterHoverVariantColors.Background,
      },
      text: {
        color: FilterHoverVariantColors.FontColor,
      },
    },
    [NotificationBadgeVariant.Popup]: {
      contentContainer: {
        bg: PopupVariantColors.Background,
      },
      text: {
        color: PopupVariantColors.FontColor,
      },
    },
    [NotificationBadgeVariant.New]: {
      contentContainer: {
        bg: NewVariantColors.Background,
      },
      text: {
        color: NewVariantColors.FontColor,
      },
    },
    [NotificationBadgeVariant.Beta]: {
      contentContainer: {
        bg: BetaVariantColors.Background,
      },
      text: {
        color: BetaVariantColors.FontColor,
      },
    },
  },
  sizes: {
    [Size.md]: {
      contentContainer: {
        minWidth: '1.5rem',
        height: '1.5rem',
        p: '0.25rem',
      },
      text: {
        lineHeight: '1.5rem',
      },
    },
    [Size.sm]: {
      contentContainer: {
        minWidth: '1.25rem',
        height: '1.25rem',
        px: '0.25rem',
        py: '0.125rem',
      },
      text: {
        lineHeight: '1.25rem',
      },
    },
    [Size.xs]: {
      contentContainer: {
        minWidth: '.95rem',
        height: '.95rem',
        px: '0.1rem',
        py: '0.075rem',
      },
      text: {
        lineHeight: '1.25rem',
      },
    },
  },
  defaultProps: {
    variant: NotificationBadgeVariant.Notification,
    size: Size.md,
  },
})

export const useNotificationBadgeStyle = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof NotificationBadgeParts>(
    getName({ NotificationBadgeStyle }),
    props,
  )
}
